import { useState, useRef, useEffect, useCallback } from 'react';
import { Text, Button, Badge, Input, Spinner, ToggleButton } from '@fluentui/react-components';
import {
  ChatRegular, SendRegular, DeleteRegular,
  BrainCircuitRegular, FolderRegular, CloudRegular,
} from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Project } from '../services/project-service';
import { useApiToken } from '../hooks/useApiToken';
import { apiUrl } from '../utils/api';

type ChatMode = 'project' | 'case' | 'general';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface ChatPageProps {
  project?: Project | null;
}

const MODE_CONFIG: Record<ChatMode, { label: string; icon: typeof FolderRegular; colour: 'brand' | 'success' | 'informative'; description: string }> = {
  project: { label: 'Project', icon: FolderRegular, colour: 'brand', description: 'Ask about this project — documents, architecture, risks, costs' },
  case: { label: 'CASE', icon: BrainCircuitRegular, colour: 'success', description: 'Structured 7-domain cloud adoption assessment' },
  general: { label: 'General', icon: CloudRegular, colour: 'informative', description: 'Azure best practices, CAF guidance, service comparisons' },
};

export function ChatPage({ project }: ChatPageProps) {
  const getToken = useApiToken();
  const [mode, setMode] = useState<ChatMode>(project ? 'project' : 'general');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Reset messages when mode changes
  useEffect(() => {
    const config = MODE_CONFIG[mode];
    setMessages([{
      role: 'system',
      content: `**${config.label} Mode** — ${config.description}${project ? ` (${project.name})` : ''}`,
    }]);
  }, [mode, project]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || sending) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setSending(true);

    // Add empty assistant message for streaming
    const assistantIdx = messages.length + 1;
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    try {
      const token = await getToken();
      let endpoint: string;
      let body: Record<string, unknown>;

      if (mode === 'case' && project) {
        endpoint = `/api/stream/assessment/${project.id}/chat?customerId=${encodeURIComponent(project.customerId)}`;
        body = { message: text };
      } else {
        endpoint = '/api/chat/general';
        body = {
          message: text,
          mode,
          customerId: project?.customerId,
          projectId: project?.id,
          conversationHistory: messages
            .filter(m => m.role !== 'system')
            .slice(-10)
            .map(m => ({ role: m.role, content: m.content })),
        };
      }

      const res = await fetch(apiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });

      if (res.ok && res.headers.get('content-type')?.includes('text/event-stream')) {
        // Stream SSE
        const reader = res.body?.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';

        if (reader) {
          let currentEvent = 'delta';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value, { stream: true });
            for (const line of chunk.split('\n')) {
              if (line.startsWith('event: ')) {
                currentEvent = line.slice(7).trim();
              } else if (line.startsWith('data: ')) {
                try {
                  const parsed = JSON.parse(line.slice(6));

                  if (currentEvent === 'tool_call' && parsed.tool && parsed.summary) {
                    // Show tool call as an inline notification in the assistant message
                    const toolNote = `\n\n> **${parsed.summary}**\n\n`;
                    accumulated += toolNote;
                    setMessages(prev => {
                      const updated = [...prev];
                      if (updated[assistantIdx]) {
                        updated[assistantIdx] = { ...updated[assistantIdx], content: accumulated };
                      }
                      return updated;
                    });
                  } else if (parsed.text) {
                    accumulated += parsed.text;
                    setMessages(prev => {
                      const updated = [...prev];
                      if (updated[assistantIdx]) {
                        updated[assistantIdx] = { ...updated[assistantIdx], content: accumulated };
                      }
                      return updated;
                    });
                  }
                } catch { /* skip */ }
              }
            }
          }
        }
      } else if (res.ok) {
        // Non-streaming response
        const data = await res.json();
        const reply = data.reply || data.message || data.content || JSON.stringify(data);
        setMessages(prev => {
          const updated = [...prev];
          if (updated[assistantIdx]) {
            updated[assistantIdx] = { ...updated[assistantIdx], content: reply };
          }
          return updated;
        });
      } else {
        throw new Error(`API error: ${res.status}`);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Error: ${err instanceof Error ? err.message : 'Unknown error'}`,
      }]);
    } finally {
      setSending(false);
    }
  }, [getToken, messages, mode, project, sending]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !sending) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleReset = () => {
    const config = MODE_CONFIG[mode];
    setMessages([{
      role: 'system',
      content: `**${config.label} Mode** — Chat reset. ${config.description}`,
    }]);
  };

  return (
    <div className="page-root chat-page">
      <div className="page-title-bar">
        <ChatRegular className="icon-title" />
        <Text size={500} weight="semibold">Chat</Text>
        <div className="title-bar-actions">
          {/* Mode toggle — like Microsoft Work/Personal */}
          <div className="chat-mode-toggle">
            {(Object.keys(MODE_CONFIG) as ChatMode[]).map(m => {
              const config = MODE_CONFIG[m];
              const Icon = config.icon;
              const isDisabled = (m === 'project' || m === 'case') && !project;
              return (
                <ToggleButton
                  key={m}
                  size="small"
                  appearance={mode === m ? 'primary' : 'outline'}
                  checked={mode === m}
                  icon={<Icon />}
                  onClick={() => setMode(m)}
                  disabled={isDisabled}
                  title={isDisabled ? 'Select a project first' : config.description}
                >
                  {config.label}
                </ToggleButton>
              );
            })}
          </div>
          <Button appearance="subtle" icon={<DeleteRegular />} onClick={handleReset} title="Reset chat" />
        </div>
      </div>

      <div className="chat-messages" ref={scrollRef}>
        {messages.map((msg, i) => (
          <div key={i} className={`chat-message chat-message--${msg.role}`}>
            {msg.role === 'system' ? (
              <div className="chat-system-card">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
              </div>
            ) : (
              <div className="chat-bubble">
                <div className="chat-bubble-header">
                  <Text size={200} weight="semibold">
                    {msg.role === 'user' ? 'You' : MODE_CONFIG[mode].label + ' Advisor'}
                  </Text>
                </div>
                <div className="chat-bubble-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="chat-message chat-message--assistant">
            <div className="chat-bubble">
              <Spinner size="tiny" label="Thinking..." />
            </div>
          </div>
        )}
      </div>

      <div className="chat-input-bar">
        <Badge size="small" color={MODE_CONFIG[mode].colour} appearance="filled">{MODE_CONFIG[mode].label}</Badge>
        <Input
          className="chat-input"
          placeholder={mode === 'case' ? 'Answer the assessment question...' : 'Ask anything...'}
          value={input}
          onChange={(_e, d) => setInput(d.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
        />
        <Button
          appearance="primary"
          icon={<SendRegular />}
          onClick={() => sendMessage(input)}
          disabled={sending || !input.trim()}
        >
          Send
        </Button>
      </div>
    </div>
  );
}
